import { ILoader } from '../../../../Assembler/dist/base/loader/iLoader.js';
import type { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { DynamicResultsJson } from '../../model/dynamicResultsJson.js';
import type { AppSiteMetadata, TemplateInfo } from '../../model/appSiteMetadata.js';

/**
 * ILoaderPreProcessJson - Loader interface specifically for EnginePreProcessJson
 *
 * This is a marker interface (like C# interfaces) - no method bodies.
 * Extends ILoader with PreProcessJson-specific getTemplate() return type and metadata access.
 * Actual implementation is in LoaderPreProcessJson and loaderPreProcessJson_*.ts partial files.
 *
 * Expected methods (in addition to ILoader methods):
 *
 * @method setDynamicResults(dynamicResults) - Sets the DynamicResultsJson instance for the current merge
 * @method getTemplate(appSite, currentTemplateName, appFileOrPlaceholderOrSlotholderName, appView?, flowStack?, configJson?)
 *   Gets a template's HtmlJson and DataJson with AppView and SearchAppSites fallback support.
 *   Returns: {htmlJson: Object, dataJson: Object|null, resolvedTemplateName: string, resolvedAppSite: string, templateInfo: Object|null}
 *
 * @method applyDynamicViewHandlersJson(appSite, appView, currentTemplateName, componentName, componentDepth, componentHtmlJson, componentDataJson, resolvedAppSite, resolvedComponentName, configJson?)
 *   Applies DynamicView handlers to wrap/modify component HtmlJson. Called by Engine after getTemplate.
 *   Returns: Object (modified HtmlJson)
 *
 * @method getAppSiteMetadata(appSite, appView?)
 *   Gets AppSiteMetadata for the specified AppSite and optional AppView.
 *   Returns: Object|null
 *
 * IMPORTANT CONTRACT:
 * - currentTemplateName MUST be a clean template name WITHOUT AppSite prefix
 * - Example: "MainContent" NOT "JsonRule5A_MainContent"
 */
export abstract class ILoaderPreProcessJson extends ILoader {
    abstract setDynamicResults(_dynamicResults: DynamicResultsJson): void;
    abstract getTemplatesApiResponse(
        _appSite: string,
        _appView: string,
        _contextJson: JsonObject,
        _serverTimeMs: number,
        _includeSearchAppSites?: boolean,
        _maskHtml?: boolean,
        _orderedTemplateNames?: string[] | null
    ): string;
    abstract getTemplate(
        _appSite: string,
        _currentTemplateName: string,
        _appFileOrPlaceholderOrSlotholderName: string,
        _appView?: string | null,
        _flowStack?: string[] | null,
        _configJson?: JsonObject | null
    ): GetTemplateResult;
    abstract getTemplateWithView(
        _appSite: string,
        _currentTemplateName: string,
        _componentName: string,
        _appView?: string | null,
        _flowStack?: string[] | null,
        _configJson?: JsonObject | null,
        _componentDepthOverride?: number | null,
        _clientMode?: boolean
    ): GetTemplateResult;
    abstract applyDynamicViewHandlersJson(
        _appSite: string,
        _appView: string,
        _currentTemplateName: string,
        _componentName: string,
        _componentDepth: number,
        _componentHtmlJson: JsonObject | null,
        _componentDataJson: JsonObject | null,
        _resolvedAppSite: string,
        _resolvedComponentName: string,
        _configJson?: JsonObject | null
    ): JsonObject | null;
    abstract getAppSiteMetadata(_appSite: string, _appView?: string | null): AppSiteMetadata | null;
    abstract getComponentFromIndex(_appSite: string, _appView: string | null, _componentName: string): TemplateInfo | null;
}

/**
 * Result type for getTemplate
 */
export interface GetTemplateResult {
    htmlJson: JsonObject | null;
    dataJson: JsonObject | null;
    resolvedTemplateName: string;
    resolvedAppSite: string;
    templateInfo: TemplateInfo | null;
}
